// ListingMapping — Settings sub-section for Wave 3 SKU alias management (S8).
//
// Sarah's flow:
// - Sees all current alias mappings (Etsy 'BLUE-MUG-L' -> master 'WOOD-ENGR-L', etc.)
// - Filter by channel (Etsy / Shopify / WC / Amazon / Manual)
// - Add new mapping: select master product + channel + alias_sku + optional store/listing
// - Delete mapping (returns alias_sku to global pool)
// - Test resolver: type any SKU + see which master product it resolves to
//
// S8 invariant: alias_sku globally UNIQUE. Server returns 409 if duplicate;
// UI surfaces error inline.

import { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Plus, Search, Trash2 } from 'lucide-react'
import { Badge } from '../../components/ui/badge'
import { Button } from '../../components/ui/button'
import {
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog'
import { Dialog } from '../../components/ui/dialog.lib'
import { apiUrl, USE_REAL_API } from '../../lib/api'
import { formatDate } from '../../lib/datetime'
import { toast } from '../../lib/toast'
import { useT } from '../../i18n'
import { listingMappingI18n } from '../../i18n/settings/listing-mapping'
import type { SettingsSurface } from './Settings'
import { DataTableShell } from '../../components/ui/data-table-shell'

// Wave-14-day-7 Task 7.4 (F-38): 'manual' channel retired — every alias now
// belongs to a concrete external integration.
type ChannelType = 'etsy' | 'shopify' | 'amazon' | 'woocommerce'

const CHANNEL_LABELS: Record<ChannelType, string> = {
  etsy: 'Etsy',
  shopify: 'Shopify',
  amazon: 'Amazon',
  woocommerce: 'WooCommerce',
}

const CHANNEL_BADGES: Record<
  ChannelType,
  'success' | 'primary' | 'warning' | 'info' | 'default'
> = {
  etsy: 'warning',
  shopify: 'success',
  amazon: 'info',
  woocommerce: 'primary',
}

type AliasRow = {
  id: number
  inventory_product_id: string
  alias_sku: string
  channel_type: ChannelType
  external_listing_id?: string
  external_store_id?: string
  active: boolean
  created_at: string
  master_sku: string | null
  product_name: string | null
}

type AliasListResponse = {
  data: AliasRow[]
  meta: { total: number }
}

type MasterProduct = {
  product_id: string
  product_name: string
  master_sku?: string
}

type AdminInventoryResponse = {
  data: Array<{
    product_id: string
    product_name: string
  }>
}

// Real backend: GET /master-catalog (seller tier-scoped, offset-paginated,
// limit <= 200). Alias inventory_product_id is a master_catalog id.
type MasterCatalogPage = {
  items: Array<{ id: string; title: string; display_code?: string | null }>
  total: number
}

const MASTER_CATALOG_PAGE_SIZE = 200
const MASTER_CATALOG_MAX_PAGES = 10

async function fetchMasterProducts(): Promise<MasterProduct[]> {
  if (!USE_REAL_API) {
    // Maket/MSW mode only: the in-memory inventory pool is keyed by the same
    // product ids the mock aliases use. There is no GET /admin/inventory on
    // the real backend (and sellers could not call an admin route anyway).
    const res = await fetch(apiUrl('/admin/inventory'))
    if (!res.ok) throw new Error(`HTTP ${res.status}`)
    const body = (await res.json()) as AdminInventoryResponse
    return body.data.map((p) => ({
      product_id: p.product_id,
      product_name: p.product_name,
    }))
  }
  const products: MasterProduct[] = []
  for (let page = 0; page < MASTER_CATALOG_MAX_PAGES; page += 1) {
    const offset = page * MASTER_CATALOG_PAGE_SIZE
    const res = await fetch(
      apiUrl(`/master-catalog?limit=${MASTER_CATALOG_PAGE_SIZE}&offset=${offset}`),
    )
    if (!res.ok) throw new Error(`HTTP ${res.status}`)
    const body = (await res.json()) as MasterCatalogPage
    for (const item of body.items) {
      products.push({
        product_id: item.id,
        product_name: item.title,
        master_sku: item.display_code ?? undefined,
      })
    }
    if (body.items.length < MASTER_CATALOG_PAGE_SIZE || products.length >= body.total) break
  }
  return products
}

type AddForm = {
  inventory_product_id: string
  alias_sku: string
  channel_type: ChannelType
  external_listing_id: string
  external_store_id: string
}

const DEFAULT_ADD_FORM: AddForm = {
  inventory_product_id: '',
  alias_sku: '',
  channel_type: 'etsy',
  external_listing_id: '',
  external_store_id: '',
}

type ResolveResult = {
  input_sku: string
  resolved: boolean
  via: 'master' | 'alias' | null
  product: { id: string; master_sku: string; name: string } | null
  alias: AliasRow | null
}

export function ListingMapping({ surface }: { surface: SettingsSurface }) {
  void surface // sellers-only at Settings shell level
  const t = useT(listingMappingI18n)
  const qc = useQueryClient()
  const [channelFilter, setChannelFilter] = useState<ChannelType | 'all'>('all')
  const [search, setSearch] = useState('')
  const [addOpen, setAddOpen] = useState(false)
  const [addForm, setAddForm] = useState<AddForm>(DEFAULT_ADD_FORM)
  const [resolverInput, setResolverInput] = useState('')
  const [resolverResult, setResolverResult] = useState<ResolveResult | null>(null)
  const [resolverLoading, setResolverLoading] = useState(false)

  const aliasesQuery = useQuery<AliasListResponse>({
    queryKey: ['inventory', 'aliases', channelFilter],
    queryFn: async () => {
      const url =
        channelFilter === 'all'
          ? apiUrl('/inventory/aliases')
          : apiUrl(`/inventory/aliases?channel=${channelFilter}`)
      const res = await fetch(url)
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      return res.json()
    },
  })

  const productsQuery = useQuery<MasterProduct[]>({
    queryKey: ['settings', 'listing-mapping', 'master-products', USE_REAL_API],
    queryFn: fetchMasterProducts,
  })

  const masterProducts: MasterProduct[] = useMemo(
    () => productsQuery.data ?? [],
    [productsQuery.data],
  )

  const createMut = useMutation({
    mutationFn: async (form: AddForm) => {
      const body: Record<string, unknown> = {
        inventory_product_id: form.inventory_product_id,
        alias_sku: form.alias_sku.trim(),
        channel_type: form.channel_type,
      }
      if (form.external_listing_id.trim()) {
        body.external_listing_id = form.external_listing_id.trim()
      }
      if (form.external_store_id.trim()) {
        body.external_store_id = form.external_store_id.trim()
      }
      const res = await fetch(apiUrl('/inventory/aliases'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      })
      const payload = await res.json().catch(() => null)
      if (!res.ok) {
        const detail = payload?.title ?? `HTTP ${res.status}`
        throw new Error(detail)
      }
      return payload
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['inventory', 'aliases'] })
      qc.invalidateQueries({ queryKey: ['admin', 'activity'] })
      toast.success(t.toasts.createdTitle, {
        description: t.toasts.createdDescription,
        audience: 'seller',
      })
      setAddOpen(false)
      setAddForm(DEFAULT_ADD_FORM)
    },
    onError: (err) => {
      toast.error(t.toasts.createFailed, {
        description: err instanceof Error ? err.message : 'unknown',
        audience: 'seller',
      })
    },
  })

  const deleteMut = useMutation({
    mutationFn: async (id: number) => {
      const res = await fetch(apiUrl(`/inventory/aliases/${id}`), {
        method: 'DELETE',
      })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      return res.json()
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['inventory', 'aliases'] })
      qc.invalidateQueries({ queryKey: ['admin', 'activity'] })
      toast.success(t.toasts.deletedTitle, {
        description: t.toasts.deletedDescription,
        audience: 'seller',
      })
    },
    onError: (err) => {
      toast.error(t.toasts.deleteFailed, {
        description: err instanceof Error ? err.message : 'unknown',
        audience: 'seller',
      })
    },
  })

  async function runResolver() {
    if (!resolverInput.trim()) return
    setResolverLoading(true)
    try {
      const res = await fetch(
        apiUrl(`/inventory/sku-resolve?sku=${encodeURIComponent(resolverInput.trim())}`),
      )
      const payload = (await res.json()) as ResolveResult
      setResolverResult(payload)
    } catch (err) {
      toast.error(t.toasts.resolverFailed, {
        description: err instanceof Error ? err.message : 'unknown',
        audience: 'seller',
      })
    } finally {
      setResolverLoading(false)
    }
  }

  const filtered = useMemo(() => {
    const rows = aliasesQuery.data?.data ?? []
    const q = search.trim().toLowerCase()
    if (!q) return rows
    return rows.filter(
      (r) =>
        r.alias_sku.toLowerCase().includes(q) ||
        r.master_sku?.toLowerCase().includes(q) ||
        r.product_name?.toLowerCase().includes(q) ||
        r.external_listing_id?.toLowerCase().includes(q) ||
        r.external_store_id?.toLowerCase().includes(q),
    )
  }, [aliasesQuery.data, search])

  return (
    <div className="space-y-6">
      <header className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-lg font-semibold text-slate-900">{t.pageTitle}</h2>
          <p className="mt-1 text-sm text-slate-600">
            {t.pageDescriptionLead}
            <strong>{t.s8InvariantLabel}</strong>
            {t.pageDescriptionTail}
          </p>
        </div>
        <Button onClick={() => setAddOpen(true)} disabled={masterProducts.length === 0}>
          <Plus className="h-4 w-4" />
          {t.addMapping}
        </Button>
      </header>

      {/* Non-canonical notice — order intake reads seller_store_skus, not these aliases */}
      <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
        {t.nonCanonicalNotice}
      </div>

      {/* Resolver — quick lookup tool */}
      <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
        <div className="flex items-center gap-2 mb-2">
          <Search className="h-4 w-4 text-slate-500" />
          <h3 className="text-sm font-medium text-slate-800">{t.resolver.title}</h3>
          <span className="text-xs text-slate-500">{t.resolver.hint}</span>
        </div>
        <div className="flex gap-2">
          <input
            type="text"
            value={resolverInput}
            onChange={(e) => setResolverInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') runResolver()
            }}
            placeholder={t.resolver.placeholder}
            className="flex-1 rounded-md border border-slate-300 px-3 py-1.5 text-sm font-mono"
          />
          <Button size="sm" onClick={runResolver} disabled={resolverLoading}>
            {resolverLoading ? t.resolver.resolving : t.resolver.resolve}
          </Button>
        </div>
        {resolverResult && (
          <div className="mt-3 text-sm">
            {resolverResult.resolved && resolverResult.product ? (
              <div className="flex items-center gap-2 text-slate-700">
                <Badge variant="success">{t.resolver.resolvedBadge(resolverResult.via ?? '')}</Badge>
                <span className="font-mono">{resolverResult.input_sku}</span>
                <span className="text-slate-400">→</span>
                <span className="font-mono">{resolverResult.product.master_sku}</span>
                <span className="text-slate-600">({resolverResult.product.name})</span>
              </div>
            ) : (
              <div className="flex items-center gap-2 text-rose-700">
                <Badge variant="destructive">{t.resolver.notFoundBadge}</Badge>
                <span className="font-mono">{resolverResult.input_sku}</span>
                <span className="text-slate-600">{t.resolver.notFoundTail}</span>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Filters */}
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-2">
          <span className="text-xs font-medium text-slate-500 uppercase tracking-wider">
            {t.filters.channelLabel}
          </span>
          <div className="flex gap-1">
            {(['all', 'etsy', 'shopify', 'amazon', 'woocommerce'] as const).map(
              (c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => setChannelFilter(c)}
                  className={
                    'rounded-md px-3 py-1 text-xs font-medium transition-colors ' +
                    (channelFilter === c
                      ? 'bg-slate-900 text-white'
                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200')
                  }
                >
                  {c === 'all' ? t.filters.all : CHANNEL_LABELS[c as ChannelType]}
                </button>
              ),
            )}
          </div>
        </div>
        <input
          type="search"
          placeholder={t.filters.searchPlaceholder}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-72 rounded-md border border-slate-200 bg-slate-50 px-3 py-1.5 text-sm focus:bg-white focus:border-slate-300 focus:outline-none focus:ring-2 focus:ring-slate-200"
        />
      </div>

      {/* Table */}
      <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] overflow-hidden">
        {aliasesQuery.isLoading && (
          <div className="px-4 py-8 text-sm text-slate-500">{t.states.loading}</div>
        )}
        {aliasesQuery.data && filtered.length === 0 && (
          <div className="px-4 py-8 text-sm text-slate-500 text-center">
            {search.trim() ? t.states.emptySearch(search) : t.states.emptyAll}
          </div>
        )}
        {filtered.length > 0 && (
          <DataTableShell variant="embedded">
          <table>
            <thead>
              <tr>
                <th>{t.columns.channel}</th>
                <th>{t.columns.aliasSku}</th>
                <th>{t.columns.master}</th>
                <th>{t.columns.storeListing}</th>
                <th>{t.columns.created}</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => (
                <tr key={r.id}>
                  <td className="py-2.5">
                    <Badge variant={CHANNEL_BADGES[r.channel_type]}>
                      {CHANNEL_LABELS[r.channel_type]}
                    </Badge>
                  </td>
                  <td className="py-2.5 font-mono text-[var(--color-text)]">{r.alias_sku}</td>
                  <td className="py-2.5">
                    <div className="font-mono text-[var(--color-text)]">{r.master_sku ?? '?'}</div>
                    <div className="text-xs text-[var(--color-text-muted)]">{r.product_name ?? '—'}</div>
                  </td>
                  <td className="py-2.5">
                    {r.external_store_id || r.external_listing_id ? (
                      <div className="text-xs">
                        {r.external_store_id && (
                          <div className="font-mono text-[var(--color-text)]">{r.external_store_id}</div>
                        )}
                        {r.external_listing_id && (
                          <div className="font-mono text-[var(--color-text-muted)]">
                            {t.table.listingPrefix}{r.external_listing_id}
                          </div>
                        )}
                      </div>
                    ) : (
                      <span className="text-xs text-[var(--color-text-muted)]">—</span>
                    )}
                  </td>
                  <td className="py-2.5 text-xs text-[var(--color-text-muted)]">
                    {formatDate(r.created_at)}
                  </td>
                  <td className="py-2.5 text-right">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => {
                        if (window.confirm(t.deleteConfirm(r.alias_sku))) {
                          deleteMut.mutate(r.id)
                        }
                      }}
                      disabled={deleteMut.isPending}
                    >
                      <Trash2 className="h-4 w-4 text-[var(--color-danger)]" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </DataTableShell>
        )}
      </div>

      <p className="text-xs text-slate-400">{t.footerNote}</p>

      {/* Add mapping dialog */}
      <Dialog
        open={addOpen}
        onOpenChange={(o) => {
          if (!o) {
            setAddOpen(false)
            setAddForm(DEFAULT_ADD_FORM)
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t.modal.title}</DialogTitle>
            <DialogDescription>{t.modal.description}</DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            <div>
              <label className="block text-xs font-medium text-slate-700 mb-1">
                {t.modal.masterProduct}
              </label>
              <select
                className="w-full rounded-md border border-slate-300 px-3 py-1.5 text-sm"
                value={addForm.inventory_product_id}
                onChange={(e) =>
                  setAddForm((f) => ({ ...f, inventory_product_id: e.target.value }))
                }
              >
                <option value="">{t.modal.masterProductPlaceholder}</option>
                {masterProducts.map((p) => (
                  <option key={p.product_id} value={p.product_id}>
                    {p.product_name} ({p.master_sku ?? p.product_id})
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-xs font-medium text-slate-700 mb-1">
                {t.modal.channel}
              </label>
              <select
                className="w-full rounded-md border border-slate-300 px-3 py-1.5 text-sm"
                value={addForm.channel_type}
                onChange={(e) =>
                  setAddForm((f) => ({
                    ...f,
                    channel_type: e.target.value as ChannelType,
                  }))
                }
              >
                {(Object.keys(CHANNEL_LABELS) as ChannelType[]).map((c) => (
                  <option key={c} value={c}>
                    {CHANNEL_LABELS[c]}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-xs font-medium text-slate-700 mb-1">
                {t.modal.aliasSku}
              </label>
              <input
                type="text"
                className="w-full rounded-md border border-slate-300 px-3 py-1.5 text-sm font-mono"
                placeholder={t.modal.aliasSkuPlaceholder}
                value={addForm.alias_sku}
                onChange={(e) => setAddForm((f) => ({ ...f, alias_sku: e.target.value }))}
              />
              <p className="mt-1 text-[11px] text-slate-500">{t.modal.namingHint}</p>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-slate-700 mb-1">
                  {t.modal.storeIdLabel}
                </label>
                <input
                  type="text"
                  className="w-full rounded-md border border-slate-300 px-3 py-1.5 text-sm font-mono"
                  placeholder={t.modal.storeIdPlaceholder}
                  value={addForm.external_store_id}
                  onChange={(e) =>
                    setAddForm((f) => ({ ...f, external_store_id: e.target.value }))
                  }
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-700 mb-1">
                  {t.modal.listingIdLabel}
                </label>
                <input
                  type="text"
                  className="w-full rounded-md border border-slate-300 px-3 py-1.5 text-sm font-mono"
                  placeholder={t.modal.listingIdPlaceholder}
                  value={addForm.external_listing_id}
                  onChange={(e) =>
                    setAddForm((f) => ({ ...f, external_listing_id: e.target.value }))
                  }
                />
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setAddOpen(false)
                setAddForm(DEFAULT_ADD_FORM)
              }}
              disabled={createMut.isPending}
            >
              {t.modal.cancel}
            </Button>
            <Button
              onClick={() => createMut.mutate(addForm)}
              disabled={
                createMut.isPending ||
                !addForm.inventory_product_id ||
                !addForm.alias_sku.trim()
              }
            >
              {createMut.isPending ? t.modal.saving : t.modal.save}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
